import {
  FlexBox,
  Icon,
  IconNames,
  SelectionList,
  Text,
  TextWithHighlight,
  Tooltip,
} from '@dixa/lib-shared-habla';

type SelectionListItemDataBase<TItemSpecificData> = TItemSpecificData & {
  readonly title: string;
  readonly icon?: IconNames;
  readonly disabled?: boolean;
  readonly tooltip?: string;
};

export type SelectionListLeafItemData = SelectionListItemDataBase<{
  readonly value: string;
}>;

export type SelectionListNodeItemData = SelectionListItemDataBase<{
  readonly value?: string;
  readonly items: readonly SelectionListItemData[];
}>;

export type SelectionListItemData =
  | SelectionListLeafItemData
  | SelectionListNodeItemData;

export function renderItem(
  item: SelectionListLeafItemData,
  highlight?: string,
): JSX.Element {
  const { value, title, icon, disabled, tooltip } = item;

  return (
    <Tooltip key={`${item.value}--item`} content={tooltip}>
      <SelectionList.Item value={value} isDisabled={disabled}>
        <Content title={title} icon={icon} highlight={highlight} />
      </SelectionList.Item>
    </Tooltip>
  );
}

export function renderList(
  item: SelectionListNodeItemData,
  highlight?: string,
): JSX.Element {
  const { value, title, icon, items, tooltip } = item;

  const parentItem = (
    <Tooltip key={`${item.value}--item`} content={tooltip}>
      <SelectionList.Item {...(value && { value })}>
        <Content title={title} icon={icon} highlight={highlight} />
      </SelectionList.Item>
    </Tooltip>
  );

  return (
    <SelectionList.List
      key={`${value}--list`}
      parentItem={parentItem}
      listProps={{ style: { minWidth: '250px' } }}
    >
      {items.map((item) => renderItemOrList(item, highlight))}
    </SelectionList.List>
  );
}

const Content = ({
  title,
  icon,
  highlight,
}: {
  title: string;
  icon?: IconNames;
  highlight?: string;
}) => (
  <FlexBox alignItems="center" gap="10px">
    {icon && (
      <FlexBox flex="0 0 16px">
        <Icon name={icon} />
      </FlexBox>
    )}
    <FlexBox
      flex="1 1 auto"
      style={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }}
    >
      <Text.Paragraph>
        <TextWithHighlight text={title} highlight={highlight} />
      </Text.Paragraph>
    </FlexBox>
  </FlexBox>
);

export function renderItemOrList(
  item: SelectionListItemData,
  highlight?: string,
): JSX.Element {
  if (isLeafItem(item)) {
    return renderItem(item, highlight);
  }

  return renderList(item, highlight);
}

export function isLeafItem(
  item: SelectionListItemData,
): item is SelectionListLeafItemData {
  return !('items' in item);
}
